import { memo, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker
} from '@vnedyalk0v/react19-simple-maps';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { blue } from '@ant-design/colors';

const geoUrl = new URL('/france.json', window.location.origin).toString();

const MapFrance = ({
  showLabel,
  showTooltip,
  markers,
  selectedZones,
  onTooltipMarker,
  onClickMarker,
  onClickZone,
  disabledHover,
  disabledPress,
  colors: { default: defaultColor, selected } = {
    default: 'var(--primaryColor)',
    selected: 'var(--clientColor)'
  },
  containerStyle
}) => {
  const [tooltip, setTooltip] = useState('');

  const getSelectedZone = (dept) => {
    if (selectedZones) {
      const zone = selectedZones.find((z) => z.dep_name === dept);

      if (zone) return zone.color ? zone.color : selected;
    }
    return defaultColor;
  };

  return (
    <div style={containerStyle}>
      <ComposableMap data-tip="">
        <ZoomableGroup
          center={[2.2137, 46.2276]}
          maxZoom={50}
          zoom={20}
          minZoom={20}
          disablePanning
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo, idx) => {
                const color = getSelectedZone(geo.properties.NAME_2);
                const tT = geo.properties.NAME_2;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => onClickZone && onClickZone(geo, idx)}
                    onMouseEnter={() => {
                      if (tT) setTooltip(tT);
                      else setTooltip('');
                    }}
                    onMouseLeave={() => setTooltip('')}
                    style={{
                      default: {
                        fill: color,
                        outline: 'none'
                      },
                      hover: {
                        fill: color,
                        opacity: disabledHover ? 1 : 0.7,
                        outline: 'none'
                      },
                      pressed: {
                        fill: color,
                        opacity: disabledPress ? 1 : 0.7,
                        outline: 'none'
                      }
                    }}
                  />
                );
              })
            }
          </Geographies>
          {markers &&
            markers.map((marker, idx) => {
              const { name, stock, fontSize, stroke, fill, coordinates, r } = {
                r: 0.5,
                fontSize: 1.5,
                fill: blue.primary,
                stroke: '#fff',
                ...marker
              };
              const markerOffset = r + fontSize - 0.2;

              return (
                <Marker key={name || idx} coordinates={coordinates || []}>
                  <circle
                    r={r}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={0.1}
                    onMouseEnter={() =>
                      onTooltipMarker
                        ? setTooltip(onTooltipMarker(marker))
                        : setTooltip(`${name}: ${stock}`)
                    }
                    onMouseLeave={() => setTooltip('')}
                    onClick={() => onClickMarker && onClickMarker(marker, idx)}
                    style={{ cursor: onClickMarker ? 'pointer' : 'default' }}
                  />
                  {showLabel && (
                    <text
                      textAnchor="middle"
                      y={markerOffset}
                      style={{
                        fontFamily: 'system-ui',
                        fill: '#5D5A6D',
                        fontSize,
                        cursor: 'default'
                      }}
                    >
                      {name}
                    </text>
                  )}
                </Marker>
              );
            })}
        </ZoomableGroup>
      </ComposableMap>
      {showTooltip && <Tooltip>{tooltip}</Tooltip>}
    </div>
  );
};

MapFrance.propTypes = {
  showLabel: PropTypes.bool,
  showTooltip: PropTypes.bool,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      coordinates: PropTypes.arrayOf(PropTypes.number),
      name: PropTypes.string,
      r: PropTypes.number,
      fill: PropTypes.string,
      stroke: PropTypes.string
    })
  ),
  selectedZones: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, color: PropTypes.string })
  ),
  onTooltipMarker: PropTypes.func,
  onClickMarker: PropTypes.func,
  onClickZone: PropTypes.func,
  colors: PropTypes.shape({
    default: PropTypes.string,
    hover: PropTypes.string,
    pressed: PropTypes.string,
    selected: PropTypes.string
  }),
  disabledPress: PropTypes.bool,
  disabledHover: PropTypes.bool,
  containerStyle: PropTypes.shape({})
};

MapFrance.defaultProps = {
  showLabel: true,
  showTooltip: true,
  markers: null,
  selectedZones: null,
  onTooltipMarker: null,
  onClickMarker: null,
  onClickZone: null,
  colors: {
    default: 'var(--primaryColor)',
    hover: 'var(--clientColor)',
    pressed: 'var(--clientColor)',
    selected: 'var(--clientColor)'
  },
  disabledPress: false,
  disabledHover: false,
  containerStyle: null
};

export default memo(MapFrance);
